<template>
  <div class="integration-recovery">
    <div class="integration-recovery__actions">
      <div v-for="action in actions" :key="action.key">
        <AppButton
          design="new"
          theme="dark"
          :disabled="busy || !allowed(action.key)"
          :aria-describedby="`recovery-${action.key}`"
          @click.native="run(action.key)"
        >
          {{ action.label }}
        </AppButton>
        <p :id="`recovery-${action.key}`" class="integration-muted">
          {{
            allowed(action.key)
              ? action.description
              : (detail.actions && detail.actions[action.key] && detail.actions[action.key].reason) ||
                'This action is unavailable for the current state.'
          }}
        </p>
      </div>
    </div>
    <div v-if="uncertain" class="integration-notice" role="status">
      <p>The request outcome is unknown. Check its status or repeat the same request safely.</p>
      <AppButton design="new" theme="dark" :disabled="posting" @click.native="run(uncertain.action)">
        {{ posting ? 'Submitting…' : 'Repeat same request' }}
      </AppButton>
    </div>
    <div v-if="command" class="integration-notice" role="status">
      <strong>
        {{ humanize(command.action) }} · {{ timedOut && active ? 'In progress' : humanize(command.status) }}
      </strong>
      <p>{{ commandMessage }}</p>
      <p v-if="command.error">{{ command.error }}</p>
      <AppButton v-if="active" design="new" theme="dark" size="small" :disabled="polling" @click.native="resume">
        Refresh command
      </AppButton>
    </div>
    <p v-if="error" class="integration-error" role="alert">{{ error }}</p>
  </div>
</template>

<script>
import { postMessageOperationRetry, postMessageOperationReconcile, postMessageOperationRetrySync, getMessageOperationCommand } from '@/http'
import { activeCommand, executionStatuses, humanize, errorMessage } from './model'
import { pendingRequest, prepareRequest, finishRequest } from './recovery'

const commands = { retry: postMessageOperationRetry, reconcile: postMessageOperationReconcile, retry_sync: postMessageOperationRetrySync }
export default {
  props: { detail: { type: Object, required: true } },
  data: () => ({
    command: null,
    uncertain: null,
    posting: false,
    polling: false,
    timedOut: false,
    error: '',
    timer: null,
    deadline: 0,
    generation: 0,
    actions: [
      { key: 'retry', label: 'Retry', description: 'Start a new provider attempt when permitted.' },
      { key: 'reconcile', label: 'Reconcile', description: 'Check the provider for an existing result.' },
      { key: 'retry_sync', label: 'Retry business updates', description: 'Apply business updates without sending again.' }
    ]
  }),
  computed: {
    active() { return activeCommand(this.command) },
    busy() { return this.posting || this.active || Boolean(this.uncertain) },
    commandMessage() {
      if (this.active) return this.timedOut ? 'Still in progress. Refresh to check again; the command continues on the server.' : 'The command was accepted. This does not confirm message delivery.'
      if (this.command.status === 'succeeded' && this.detail.integration === 'knowledge_ai') return 'A new retry cycle is queued. The operation status shows the Knowledge AI result.'
      if (this.command.status === 'succeeded') return this.command.action === 'reconcile' && this.detail.execution_status === 'unknown' ? 'No conclusive result found.' : 'Command completed. Review the refreshed execution, delivery and business update statuses.'
      return 'Review the current operation before trying another action.'
    }
  },
  watch: {
    'detail.last_command': {
      immediate: true,
      handler(command) {
        if (!command || this.posting) return
        const wasActive = this.active
        const sameActiveCommand = this.command && command.id === this.command.id && wasActive && activeCommand(command)
        this.command = command
        if (sameActiveCommand) return
        if (this.active) this.resume()
        else {
          clearTimeout(this.timer)
          if (wasActive) this.$emit('changed')
        }
      }
    }
  },
  mounted() {
    this.uncertain = pendingRequest(this.detail.id)
    document.addEventListener('visibilitychange', this.visibilityChanged)
  },
  beforeDestroy() { this.generation++; clearTimeout(this.timer); document.removeEventListener('visibilitychange', this.visibilityChanged) },
  methods: {
    humanize,
    allowed(action) {
      return executionStatuses.includes(this.detail.execution_status) && this.detail.actions?.[action]?.allowed === true
    },
    async run(action) {
      if (this.posting || this.active || (!this.uncertain && !this.allowed(action))) return
      const request = this.uncertain || prepareRequest(this.detail, action)
      const generation = ++this.generation
      this.posting = true
      this.error = ''
      this.uncertain = request
      try {
        const response = await commands[request.action](this.detail.id, request.body)
        finishRequest(this.detail.id, request)
        if (generation !== this.generation) return
        this.uncertain = null
        this.command = response.data
        if (this.active) this.resume()
        else this.$emit('changed')
      } catch (error) {
        const status = error?.response?.status
        if ([400, 403, 404].includes(status)) finishRequest(this.detail.id, request)
        if (generation !== this.generation) return
        if ([400, 403, 404].includes(status)) {
          this.uncertain = null
          this.error = errorMessage(error, 'Operation')
          if (status === 403) this.$emit('denied')
          else this.$emit('refresh')
        } else this.error = 'Could not confirm whether the command was accepted. No new request has been created.'
      } finally {
        if (generation === this.generation) this.posting = false
      }
    },
    resume() {
      clearTimeout(this.timer)
      this.deadline = Date.now() + 60000
      this.timedOut = false
      this.error = ''
      this.poll()
    },
    schedule() {
      clearTimeout(this.timer)
      if (!this.active || document.hidden) return
      if (Date.now() >= this.deadline) { this.timedOut = true; return }
      this.timer = setTimeout(this.poll, 2000)
    },
    async poll() {
      if (!this.active || this.polling || document.hidden) return
      const generation = this.generation
      const commandId = this.command.id
      this.polling = true
      try {
        const response = await getMessageOperationCommand(this.detail.id, commandId)
        if (generation !== this.generation || commandId !== this.command.id) return
        this.command = response.data
        if (this.active) this.schedule()
        else {
          clearTimeout(this.timer)
          this.$emit('changed')
        }
      } catch (error) {
        if (generation !== this.generation) return
        this.error = errorMessage(error, 'Command')
        if (error?.response?.status === 403) this.$emit('denied')
        // An unavailable poll is not a failed command. Resume only on Refresh.
      } finally {
        if (generation === this.generation) this.polling = false
      }
    },
    visibilityChanged() {
      clearTimeout(this.timer)
      if (!document.hidden) this.schedule()
    }
  }
}
</script>
